import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { getShippingRates, createCheckoutSession } from "../lib/api";

const initialAddress = {
    name: "",
    email: "",
    street1: "",
    street2: "",
    city: "",
    state: "",
    zip: "",
    country: "US",
    phone: "",
};

const CheckoutAddress = () => {
    const { items, count, subtotal, setOpen } = useCart();
    const navigate = useNavigate();
    const [addr, setAddr] = useState(initialAddress);
    const [step, setStep] = useState("address"); // 'address' | 'rate'
    const [rates, setRates] = useState([]);
    const [selectedRate, setSelectedRate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [ratesInfo, setRatesInfo] = useState(null);

    useEffect(() => {
        // If no items, bounce
        if (items.length === 0) {
            const t = setTimeout(() => navigate("/shop"), 100);
            return () => clearTimeout(t);
        }
        setOpen(false);
    }, [items.length, navigate, setOpen]);

    const handle = (k) => (e) => setAddr({ ...addr, [k]: e.target.value });

    const submitAddress = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                items: items.map((i) => ({
                    product_id: i.product.id,
                    quantity: i.quantity,
                })),
                address: addr,
            };
            const data = await getShippingRates(payload);
            setRates(data.rates || []);
            setRatesInfo(data);
            if (data.rates && data.rates.length > 0) {
                setSelectedRate(data.rates[0].shippo_rate_id);
            }
            setStep("rate");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Could not fetch shipping rates. Check your address.",
            );
        } finally {
            setLoading(false);
        }
    };

    const submitCheckout = async () => {
        if (!selectedRate) return toast.error("Pick a shipping option");
        setLoading(true);
        try {
            const { url } = await createCheckoutSession({
                items: items.map((i) => ({
                    product_id: i.product.id,
                    quantity: i.quantity,
                })),
                address: addr,
                shippo_rate_id: selectedRate,
            });
            if (url) window.location.href = url;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Checkout failed");
        } finally {
            setLoading(false);
        }
    };

    if (items.length === 0)
        return (
            <div className="max-w-2xl mx-auto px-6 py-24 text-center text-[#5C6A79]">
                Your cart is empty. Redirecting…
            </div>
        );

    return (
        <div
            data-testid="page-checkout-address"
            className="max-w-6xl mx-auto px-6 md:px-12 lg:px-20 py-10 md:py-16"
        >
            <button
                onClick={() => navigate(-1)}
                data-testid="checkout-back-btn"
                className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#5C6A79] hover:text-[#0A2342] mb-10"
            >
                <ArrowLeft size={14} /> Back
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 lg:gap-20">
                <div>
                    <p className="text-[11px] tracking-[0.35em] uppercase text-[#9E5A35] mb-4">
                        Checkout
                    </p>
                    <h1 className="font-serif-display text-4xl md:text-5xl text-[#0A2342] leading-[1.05] mb-10">
                        {step === "address"
                            ? "Where should we send it?"
                            : "Choose your shipping."}
                    </h1>

                    {step === "address" && (
                        <form
                            data-testid="checkout-address-form"
                            onSubmit={submitAddress}
                            className="space-y-5"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <Field
                                    label="Full name"
                                    testId="chk-name"
                                    required
                                    value={addr.name}
                                    onChange={handle("name")}
                                />
                                <Field
                                    label="Email"
                                    testId="chk-email"
                                    type="email"
                                    required
                                    value={addr.email}
                                    onChange={handle("email")}
                                />
                            </div>
                            <Field
                                label="Address line 1"
                                testId="chk-street1"
                                required
                                value={addr.street1}
                                onChange={handle("street1")}
                            />
                            <Field
                                label="Address line 2 (optional)"
                                testId="chk-street2"
                                value={addr.street2}
                                onChange={handle("street2")}
                            />
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                <Field
                                    label="City"
                                    testId="chk-city"
                                    required
                                    value={addr.city}
                                    onChange={handle("city")}
                                />
                                <Field
                                    label="State"
                                    testId="chk-state"
                                    required
                                    value={addr.state}
                                    onChange={handle("state")}
                                />
                                <Field
                                    label="ZIP"
                                    testId="chk-zip"
                                    required
                                    value={addr.zip}
                                    onChange={handle("zip")}
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <label className="text-[11px] tracking-[0.22em] uppercase text-[#5C6A79]">
                                        Country
                                    </label>
                                    <select
                                        data-testid="chk-country"
                                        required
                                        value={addr.country}
                                        onChange={handle("country")}
                                        className="mt-2 w-full bg-white border border-[#E5E0D8] px-4 py-3 text-sm focus:outline-none focus:border-[#0A2342]"
                                    >
                                        <option value="US">United States</option>
                                        <option value="CA">Canada</option>
                                    </select>
                                </div>
                                <Field
                                    label="Phone"
                                    testId="chk-phone"
                                    value={addr.phone}
                                    onChange={handle("phone")}
                                />
                            </div>

                            <div className="pt-4">
                                <button
                                    data-testid="chk-continue-btn"
                                    type="submit"
                                    disabled={loading}
                                    className="btn-primary w-full sm:w-auto justify-center disabled:opacity-40"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2
                                                className="animate-spin"
                                                size={16}
                                            />{" "}
                                            Getting rates…
                                        </>
                                    ) : (
                                        "Continue to Shipping"
                                    )}
                                </button>
                            </div>
                        </form>
                    )}

                    {step === "rate" && (
                        <div
                            data-testid="checkout-rate-step"
                            className="space-y-6"
                        >
                            {ratesInfo?.free_shipping && (
                                <div
                                    data-testid="free-shipping-banner"
                                    className="bg-[#0A2342] text-[#FBF9F6] px-5 py-3 text-sm rounded-sm"
                                >
                                    <span className="text-[11px] tracking-[0.22em] uppercase mr-2">
                                        Complimentary
                                    </span>
                                    Free shipping on orders of 4+ fobs.
                                </div>
                            )}

                            <ul
                                data-testid="rate-options"
                                className="space-y-3"
                            >
                                {rates.map((r) => (
                                    <li
                                        key={r.shippo_rate_id}
                                        data-testid={`rate-option-${r.shippo_rate_id.slice(0, 8)}`}
                                    >
                                        <button
                                            onClick={() =>
                                                setSelectedRate(r.shippo_rate_id)
                                            }
                                            className={`w-full text-left border rounded-sm px-5 py-4 transition-all flex items-center justify-between ${
                                                selectedRate === r.shippo_rate_id
                                                    ? "border-[#0A2342] bg-white ring-1 ring-[#0A2342]"
                                                    : "border-[#E5E0D8] bg-white hover:border-[#0A2342]"
                                            }`}
                                        >
                                            <div>
                                                <div className="text-sm font-medium text-[#0A2342]">
                                                    {r.label}
                                                </div>
                                                {r.estimated_days && (
                                                    <div className="text-xs text-[#5C6A79] mt-0.5">
                                                        Est. {r.estimated_days}{" "}
                                                        day
                                                        {r.estimated_days === 1
                                                            ? ""
                                                            : "s"}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="font-serif-display text-xl text-[#0A2342]">
                                                    {r.amount === 0
                                                        ? "Free"
                                                        : `$${r.amount.toFixed(2)}`}
                                                </span>
                                                {selectedRate ===
                                                    r.shippo_rate_id && (
                                                    <Check
                                                        className="text-[#9E5A35]"
                                                        size={18}
                                                    />
                                                )}
                                            </div>
                                        </button>
                                    </li>
                                ))}
                            </ul>

                            <div className="flex gap-3 pt-4">
                                <button
                                    data-testid="chk-back-address-btn"
                                    onClick={() => setStep("address")}
                                    className="btn-outline"
                                >
                                    Edit Address
                                </button>
                                <button
                                    data-testid="chk-pay-btn"
                                    onClick={submitCheckout}
                                    disabled={loading || !selectedRate}
                                    className="btn-primary disabled:opacity-40"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2
                                                className="animate-spin"
                                                size={16}
                                            />{" "}
                                            Redirecting…
                                        </>
                                    ) : (
                                        "Continue to Payment"
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Summary */}
                <aside className="bg-white border border-[#E5E0D8] rounded-sm p-6 h-fit">
                    <div className="text-[11px] tracking-[0.22em] uppercase text-[#5C6A79] mb-4">
                        Your Order ({count})
                    </div>
                    <ul className="space-y-3 mb-5">
                        {items.map(({ product, quantity }) => (
                            <li
                                key={product.id}
                                className="flex items-center gap-3 text-sm"
                            >
                                <div className="w-12 h-14 bg-[#FBF9F6] border border-[#E5E0D8] flex items-center justify-center overflow-hidden shrink-0">
                                    {product.image_url ? (
                                        <img
                                            src={product.image_url}
                                            alt={product.name}
                                            className="w-full h-full object-contain"
                                        />
                                    ) : (
                                        <span className="font-serif-display text-sm text-[#0A2342]">
                                            C&amp;O
                                        </span>
                                    )}
                                </div>
                                <div className="flex-1">
                                    <div className="text-[#0A2342] leading-tight">
                                        {product.name}
                                    </div>
                                    <div className="text-xs text-[#5C6A79]">
                                        Qty {quantity}
                                    </div>
                                </div>
                                <span className="text-sm text-[#0A2342]">
                                    ${(product.price * quantity).toFixed(2)}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <div className="border-t border-[#E5E0D8] pt-4 space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span className="text-[#5C6A79]">Subtotal</span>
                            <span className="text-[#0A2342]">
                                ${subtotal.toFixed(2)}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-[#5C6A79]">Shipping</span>
                            <span className="text-[#0A2342]">
                                {step === "address"
                                    ? "—"
                                    : ratesInfo?.free_shipping
                                      ? "Free"
                                      : selectedRate
                                        ? `$${rates.find((r) => r.shippo_rate_id === selectedRate)?.amount.toFixed(2)}`
                                        : "—"}
                            </span>
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
};

const Field = ({ label, testId, ...rest }) => (
    <div>
        <label className="text-[11px] tracking-[0.22em] uppercase text-[#5C6A79]">
            {label}
        </label>
        <input
            data-testid={testId}
            {...rest}
            className="mt-2 w-full bg-white border border-[#E5E0D8] px-4 py-3 text-sm focus:outline-none focus:border-[#0A2342]"
        />
    </div>
);

export default CheckoutAddress;
